/* ═══════════════════════════════════════════════════════
   Monmouth Cards — Singles Collection Filters (Player / Team)
   assets/monmouth-singles-filters.js

   Player: search + autocomplete, single pick, navigates immediately
   (only one player makes sense to filter by at a time).

   Team: search + autocomplete + removable chips, multi-select — picking
   a team adds it to window.MC.multiSelect's 'team' category instead of
   navigating directly, so "Lakers or Celtics" is handled by the OR-merge
   engine in monmouth-collection.js the same way Grading Company/Grade/
   Brand are. This also fixes the earlier version's other complaint: a
   compact search box instead of an always-rendered ~488-team list.

   Both are backed by monmouth-singles-filters.json (player/team names
   pulled from the tagged catalog).

   Re-entrant: window.MC.initSinglesFilters() is called both on first
   page load and after every AJAX section-replace (monmouth-collection.js),
   since that swap destroys and recreates this markup from scratch.
   ═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var filterDataPromise = null;
  /* Real fix (player-autosuggest-race-condition-and-brand-filter-merge-
     prompt.md, Bug 1) — lets wireAutocomplete's input handler show a
     brief "Loading…" state only while the fetch is genuinely still in
     flight, instead of jumping straight to the empty-result copy (which
     looked identical to a real zero-match and was misleading on a cold
     first load). */
  var filterDataReady = false;

  function loadFilterData() {
    if (filterDataPromise) return filterDataPromise;
    var url = window.MC_SINGLES_FILTER_DATA_URL;
    if (!url) {
      filterDataReady = true;
      return Promise.resolve({ players: [], teams: [] });
    }
    filterDataPromise = fetch(url)
      .then(function (r) { return r.json(); })
      .catch(function (err) {
        console.warn('[MC] Failed to load singles filter data:', err);
        return { players: [], teams: [] };
      });
    filterDataPromise.then(function () { filterDataReady = true; });
    return filterDataPromise;
  }

  /* Mirrors Shopify/Liquid's `handleize` filter — reuse the shared copy
     monmouth-collection.js already exposes rather than duplicating it. */
  function handleize(str) {
    if (window.MC && typeof window.MC.handleize === 'function') return window.MC.handleize(str);
    return String(str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /* Real fix (player-autosuggest-race-condition-and-brand-filter-merge-
     prompt.md, Bug 2) — data-request-search reflects Liquid's
     request.search, which this store never populates for a real query
     string (confirmed live). Reuse monmouth-collection.js's own fix for
     this (currentPreservedSearch, exposed on window.MC) so Player's tag
     toggle preserves an active Brand/Price/Great-Deal filter exactly
     like every other tag-path filter now does, instead of duplicating
     the same broken data-attribute read. */
  function getSidebarState() {
    var aside = document.querySelector('.mc-sidebar');
    if (!aside) return null;
    var currentTagsRaw = aside.getAttribute('data-current-tags') || '';
    var requestSearch = (window.MC && typeof window.MC.currentPreservedSearch === 'function')
      ? window.MC.currentPreservedSearch()
      : (aside.getAttribute('data-request-search') || '');
    return {
      collectionUrl: aside.getAttribute('data-collection-url') || '',
      currentTags: currentTagsRaw ? currentTagsRaw.split('|') : [],
      requestSearch: requestSearch
    };
  }

  /* Player-only: same toggle logic as snippets/monmouth-tag-filter-url.liquid
     — add the tag if it isn't currently active, drop it if it is,
     preserving every other currently-active single-value tag. Team no
     longer uses this (it goes through the multi-select engine instead,
     since more than one team can be active at once). */
  function buildTagToggleUrl(tagValue) {
    var state = getSidebarState();
    if (!state) return '#';
    var targetHandle = handleize(tagValue);
    var newTags = [];
    var alreadyHas = false;
    state.currentTags.forEach(function (t) {
      if (handleize(t) === targetHandle) {
        alreadyHas = true;
      } else {
        newTags.push(handleize(t));
      }
    });
    if (!alreadyHas) newTags.push(targetHandle);
    var path = newTags.length ? '/' + newTags.join('+') : '';
    return state.collectionUrl + path + state.requestSearch;
  }

  function navigateToTag(tagValue) {
    var url = buildTagToggleUrl(tagValue);
    if (window.MC && typeof window.MC.fetchAndReplace === 'function') {
      window.MC.fetchAndReplace(url);
    } else {
      window.location.href = url;
    }
  }

  function escapeHtml(s) {
    var div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  /* ══════════════════════════════════════════════
     Shared "click outside closes the dropdown" listener.
     Installed once (not per re-init). Real fix — snippets/monmouth-
     filter-sidebar.liquid can now render twice on one page (desktop
     sidebar + mobile filter sheet), so this can no longer assume a
     single input/box pair by id; instead it re-queries every rendered
     [data-role] instance each click, scoped to its own .mc-sidebar__
     search-wrap, so each instance's dropdown dismisses independently
     and never leaks/holds a stale reference to elements a previous AJAX
     swap already threw away.
  ══════════════════════════════════════════════ */
  var DISMISS_ROLES = [
    { inputRole: 'player-search', boxRole: 'player-suggestions' },
    { inputRole: 'team-search', boxRole: 'team-suggestions' }
  ];

  function installGlobalDismissListener() {
    if (window._mcSinglesDismissInstalled) return;
    window._mcSinglesDismissInstalled = true;
    document.addEventListener('click', function (e) {
      DISMISS_ROLES.forEach(function (pair) {
        document.querySelectorAll('[data-role="' + pair.inputRole + '"]').forEach(function (input) {
          var scope = input.closest('.mc-sidebar__search-wrap') || input.parentElement;
          var box = scope ? scope.querySelector('[data-role="' + pair.boxRole + '"]') : null;
          if (box && !box.contains(e.target) && e.target !== input) {
            box.hidden = true;
          }
        });
      });
    });
  }

  /* Generic autocomplete-dropdown wiring shared by Player and Team —
     only what happens when a suggestion is picked differs (onChoose).
     Real fix — takes the actual input/box elements for ONE rendered
     instance (desktop sidebar or mobile filter sheet), not ids; the
     snippet can render more than once on a page now, so each instance
     gets its own independently-wired dropdown. */
  function wireAutocomplete(opts) {
    var input = opts.input;
    var box = opts.box;
    if (!input || !box) return;

    var currentMatches = [];
    var activeIndex = -1;

    function renderSuggestions(matches, query) {
      currentMatches = matches;
      activeIndex = -1;
      if (!matches.length) {
        box.innerHTML = query
          ? '<div class="mc-sidebar__suggestion mc-sidebar__suggestion--empty">' + escapeHtml(opts.emptyLabel) + '</div>'
          : '';
        box.hidden = !query;
        return;
      }
      box.innerHTML = matches.map(function (name, i) {
        return '<button type="button" class="mc-sidebar__suggestion" data-index="' + i + '">' + escapeHtml(name) + '</button>';
      }).join('');
      box.hidden = false;
    }

    function highlight(items) {
      items.forEach(function (el, i) {
        el.classList.toggle('mc-sidebar__suggestion--active', i === activeIndex);
      });
    }

    function choose(name) {
      if (!name) return;
      box.hidden = true;
      opts.onChoose(name, input);
    }

    input.addEventListener('input', function () {
      var query = input.value.trim().toLowerCase();
      if (!query) { renderSuggestions([], ''); return; }
      /* Real fix (player-autosuggest-race-condition-and-brand-filter-
         merge-prompt.md, Bug 1) — a real race condition, confirmed live:
         loadFilterData()'s fetch is only kicked off by the FIRST
         keystroke and takes real time on a genuinely cold load (no
         browser cache yet). Two things used to be missing:
         1. Nothing told the visitor a real fetch was in flight — the
            box just stayed exactly as it did before this keystroke
            (usually empty/hidden) until the promise resolved, which
            reads as broken/unresponsive rather than loading.
         2. The match was filtered against `query` — the value captured
            in THIS keystroke's own closure — not whatever the input
            actually holds once the fetch resolves. Typing further, or
            deleting characters, while the fetch is still pending meant
            an earlier, now-stale keystroke's .then() could still fire
            (every .then() ever attached to a promise fires on
            resolution, in attachment order) and render results for text
            the visitor no longer has typed.
         Fixed by re-reading input.value fresh inside the callback
         (never trusting the closure's `query` for the actual filtering)
         and showing a real "Loading…" row while filterDataReady is
         still false. */
      if (!filterDataReady) {
        box.innerHTML = '<div class="mc-sidebar__suggestion mc-sidebar__suggestion--empty">Loading…</div>';
        box.hidden = false;
      }
      loadFilterData().then(function (data) {
        var liveQuery = input.value.trim().toLowerCase();
        if (!liveQuery) { renderSuggestions([], ''); return; }
        var pool = opts.getPool(data);
        var matches = pool
          .filter(function (name) { return name.toLowerCase().indexOf(liveQuery) !== -1; })
          .slice(0, 8);
        renderSuggestions(matches, liveQuery);
      });
    });

    box.addEventListener('click', function (e) {
      var btn = e.target.closest('.mc-sidebar__suggestion');
      if (!btn || btn.classList.contains('mc-sidebar__suggestion--empty')) return;
      choose(currentMatches[parseInt(btn.dataset.index, 10)]);
    });

    input.addEventListener('keydown', function (e) {
      if (box.hidden || !currentMatches.length) return;
      var items = box.querySelectorAll('.mc-sidebar__suggestion:not(.mc-sidebar__suggestion--empty)');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        highlight(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        highlight(items);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0) {
          e.preventDefault();
          choose(currentMatches[activeIndex]);
        }
      } else if (e.key === 'Escape') {
        box.hidden = true;
      }
    });
  }

  /* ══════════════════════════════════════════════
     PLAYER — search + autocomplete, single pick
  ══════════════════════════════════════════════ */
  /* Real fix — takes the .mc-sidebar instance to wire (desktop sidebar
     or mobile filter sheet's own rendered copy), scoped via
     [data-role] instead of a fixed id — see wireAutocomplete's comment. */
  function initPlayerSearch(sidebar) {
    wireAutocomplete({
      input: sidebar.querySelector('[data-role="player-search"]'),
      box: sidebar.querySelector('[data-role="player-suggestions"]'),
      emptyLabel: 'No players found',
      getPool: function (data) { return data.players || []; },
      onChoose: function (name, input) {
        input.value = name;
        /* 2026-07-16: was navigateToTag(name), which built its toggle
           URL from data-current-tags alone — stale the moment any
           multi-select category (Grading Company, Team, ...) has 2+
           values active, since that state only lives in
           window.MC.multiSelect, not in data-current-tags. Routing
           through multiSelect.toggle (category: null, same as how
           Condition/Year work) keeps Player correctly preserving
           whatever else is currently selected. */
        if (window.MC && window.MC.multiSelect) {
          window.MC.multiSelect.toggle(name, 'player');   /* 'player' = its filter group (OR with other players, AND with other groups) */
        } else {
          navigateToTag(name);
        }
      }
    });
  }

  /* ══════════════════════════════════════════════
     TEAM — search + autocomplete + multi-select chips
  ══════════════════════════════════════════════ */
  function renderTeamChipsForSidebar(sidebar) {
    var container = sidebar.querySelector('[data-role="team-chips"]');
    if (!container) return;
    var state = window.MC && window.MC.multiSelect ? window.MC.multiSelect.getState() : null;
    var selected = (state && state.multi && state.multi.team) || [];

    container.innerHTML = selected.map(function (team) {
      return '<button type="button" class="mc-sidebar__chip" data-team-chip="1">' +
        escapeHtml(team) +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>';
    }).join('');

    Array.from(container.children).forEach(function (chip, i) {
      chip.addEventListener('click', function () {
        if (window.MC && window.MC.multiSelect) window.MC.multiSelect.toggle(selected[i], 'team');
      });
    });
  }

  /* Public hook — no arg means "every rendered .mc-sidebar instance",
     same effective behavior as before this fix (there was only ever one
     instance previously); real fix internally fans out per-instance now
     that there can be more than one. */
  function renderTeamChips() {
    document.querySelectorAll('.mc-sidebar').forEach(renderTeamChipsForSidebar);
  }

  function initTeamSearch(sidebar) {
    var input = sidebar.querySelector('[data-role="team-search"]');
    if (input) {
      wireAutocomplete({
        input: input,
        box: sidebar.querySelector('[data-role="team-suggestions"]'),
        emptyLabel: 'No teams found',
        getPool: function (data) {
          var state = window.MC && window.MC.multiSelect ? window.MC.multiSelect.getState() : null;
          var selected = (state && state.multi && state.multi.team) || [];
          var selectedHandles = selected.map(handleize);
          return (data.teams || []).filter(function (t) { return selectedHandles.indexOf(handleize(t)) === -1; });
        },
        onChoose: function (name, inputEl) {
          inputEl.value = '';
          if (window.MC && window.MC.multiSelect) window.MC.multiSelect.toggle(name, 'team');
        }
      });
    }
    renderTeamChipsForSidebar(sidebar);
  }

  /* Real fix — fans out over every rendered .mc-sidebar instance on the
     page (desktop sidebar, and now the mobile filter sheet's own copy)
     instead of assuming exactly one. Re-entrant same as before: called
     on first load and after every AJAX section-replace, and each
     instance still present after a swap gets freshly re-wired. */
  function init() {
    document.querySelectorAll('.mc-sidebar').forEach(function (sidebar) {
      initPlayerSearch(sidebar);
      initTeamSearch(sidebar);
    });
    installGlobalDismissListener();
  }

  window.MC = window.MC || {};
  window.MC.initSinglesFilters = init;
  window.MC.renderTeamChips = renderTeamChips;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
